import { useCallback, useEffect, useRef, useState } from 'react';
import { getAudioFile } from '../../services/api';

export type VideoRenderStatus = 'idle' | 'pending' | 'ready' | 'failed';

export interface VideoRenderState {
  status: VideoRenderStatus;
  videoUrl?: string;
  failureReason?: string;
}

interface UseVideoRenderPollingResult {
  state: VideoRenderState;
  startPolling: (fileId: string) => void;
}

// 2s tick, 120 attempts → ≈4 min ceiling. ffmpeg work on a 10-min track plus
// S3 PUT lands well inside this window; runaways fall through to idle so the
// UI stops spinning forever.
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 120;
const MAX_ERROR_ATTEMPTS = 5;

/**
 * Polls a file's video_size after a share-video render is enqueued.
 *
 * Terminal states (`videoSize > 0` → ready, `videoSize === -2` → failed)
 * stop the interval and surface the result via the `state` field, which the
 * caller can fold back into its central track store. `videoSize === -1`
 * keeps polling. Anything else (missing, 0, undefined) is treated as idle
 * and ends the poll — defensive against a server that resets the row.
 */
export function useVideoRenderPolling(): UseVideoRenderPollingResult {
  const [state, setState] = useState<VideoRenderState>({ status: 'idle' });
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  const startPolling = useCallback((targetFileId: string) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    setState({ status: 'pending' });
    let attempts = 0;
    let errorCount = 0;

    const interval = setInterval(async () => {
      attempts++;
      try {
        const fileData = await getAudioFile(targetFileId);
        const size = fileData.videoSize;

        if (typeof size === 'number' && size > 0) {
          clearInterval(interval);
          pollingRef.current = null;
          setState({ status: 'ready', videoUrl: fileData.videoUrl });
          return;
        }

        if (size === -2) {
          clearInterval(interval);
          pollingRef.current = null;
          setState({
            status: 'failed',
            failureReason: fileData.videoFailureReason,
          });
          return;
        }

        if (attempts >= MAX_POLL_ATTEMPTS) {
          clearInterval(interval);
          pollingRef.current = null;
          setState({ status: 'idle' });
        }
      } catch (error) {
        console.error('[Video Render Poll] Error:', error);
        errorCount++;
        if (errorCount >= MAX_ERROR_ATTEMPTS) {
          clearInterval(interval);
          pollingRef.current = null;
          setState({ status: 'idle' });
        }
      }
    }, POLL_INTERVAL_MS);

    pollingRef.current = interval;
  }, []);

  return { state, startPolling };
}
